import { useCallback, useEffect, useState } from 'react';
import type {
  DocumentoDetalle, DocumentoResumen, ErrorCarga, EstadoDocumento, FacturacionApi, FiltroDocumentos, Pagina, TipoDocumento,
} from '../api/types';
import { RX } from '../validacion';
import { Contador, sinNeutro } from './ProductosSeccion';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from './ui';

const ESTADO: Record<EstadoDocumento, { texto: string; tono: 'ok' | 'pendiente' | 'error' | 'neutro'; ayuda: string }> = {
  PENDIENTE: { texto: 'Pendiente', tono: 'neutro', ayuda: 'En cola para enviarse.' },
  ENVIADO: { texto: 'Enviado', tono: 'pendiente', ayuda: 'Recibido por el PAC; esperando la aprobación de la DGI.' },
  AUTORIZADO: { texto: 'Aprobado', tono: 'ok', ayuda: 'Aprobado por la DGI. Se envía al cliente por correo.' },
  RECHAZADO: { texto: 'Rechazado', tono: 'error', ayuda: 'Rechazado por el PAC o la DGI. Revise los mensajes.' },
  ERROR: { texto: 'Error', tono: 'error', ayuda: 'No se pudo enviar. Corrija la causa y pulse Reintentar.' },
};

const TIPO: Record<TipoDocumento, string> = { FACTURA: 'Factura', NOTA_CREDITO: 'Nota de crédito', NOTA_DEBITO: 'Nota de débito' };

const ANULACION: Record<string, string> = {
  PENDIENTE: 'Anulación en proceso',
  ANULADA: 'Anulada ante la DGI',
  NOTA_CREDITO: 'Revertida con nota de crédito',
  NO_APLICA: 'Anulación no aplica',
  ERROR: 'Anulación con error',
};

const usd = (n: number) => 'USD ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fechaHora = (s: string | null) => (s ? new Date(s).toLocaleString('es-PA', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function DocumentosSeccion({ api, idEmpresa }: { api: FacturacionApi; idEmpresa: number }) {
  const [filtro, setFiltro] = useState<FiltroDocumentos>({ pagina: 1 });
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState<Pagina<DocumentoResumen> | null>(null);
  const [errores, setErrores] = useState<ErrorCarga[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [abierto, setAbierto] = useState<number | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    api.documentos(idEmpresa, filtro).then(setPagina).catch(setError);
    api.erroresCarga(idEmpresa).then(setErrores).catch(() => setErrores([]));
  }, [api, idEmpresa, filtro]);

  useEffect(cargar, [cargar]);

  const totalPaginas = pagina ? Math.max(1, Math.ceil(pagina.total / pagina.tamano)) : 1;
  const conteo = pagina?.conteo ?? {};

  return (
    <section className="seccion">
      <Guia
        id="documentos"
        titulo="Cómo dar seguimiento a sus documentos"
        pasos={[
          <>Las facturas se emiten solas: su punto de venta las registra y el sistema las envía en pocos minutos.</>,
          <>
            Estados: <b>Pendiente</b> (en cola) → <b>Enviado</b> (esperando a la DGI) → <b>Aprobado</b> (aprobado por la DGI y
            enviado por correo al cliente). Si queda en <b>Rechazado</b> o <b>Error</b>, abra el documento para ver el motivo.
          </>,
          <>
            Abra un documento para descargar el <b>PDF</b> y el <b>XML</b>, reenviar el correo (por ejemplo, a otra dirección) o
            <b> Reintentar</b> si quedó en Error después de corregir la causa.
          </>,
          <>
            Anulaciones: si anula una venta en su punto de venta dentro de los 7 días siguientes a su emisión, se registra la
            anulación ante la DGI. Después de ese plazo se emite automáticamente una nota de crédito (hasta 180 días).
          </>,
          <>
            <b>Facturas no cargadas</b>: ventas que no entraron a la cola por un dato faltante (por ejemplo, un producto sin código
            CPBS). Corrija el dato y pulse <b>Volver a cargar</b>.
          </>,
        ]}
      />

      <header className="seccion__cab">
        <div>
          <h2>Documentos electrónicos</h2>
          <p className="texto-sec">Facturas, notas de crédito y notas de débito emitidas. Conteo de los últimos 30 días.</p>
        </div>
        <Boton tipo="secundario" onClick={cargar}>Actualizar</Boton>
      </header>

      <ErrorApi error={error} />

      {errores.length > 0 && <FacturasNoCargadas api={api} idEmpresa={idEmpresa} errores={errores} onCambio={setErrores} />}

      <div className="contadores">
        <Contador valor={Object.values(conteo).reduce((a, b) => a + b, 0)} texto="Todos" activo={!filtro.estado} onClick={() => setFiltro({ ...filtro, estado: '', pagina: 1 })} />
        {(Object.keys(ESTADO) as EstadoDocumento[]).map((e) => (
          <Contador
            key={e}
            valor={conteo[e] ?? 0}
            texto={ESTADO[e].texto}
            tono={sinNeutro(ESTADO[e].tono)}
            activo={filtro.estado === e}
            onClick={() => setFiltro({ ...filtro, estado: e, pagina: 1 })}
          />
        ))}
      </div>

      <form
        className="filtros"
        onSubmit={(e) => {
          e.preventDefault();
          setFiltro({ ...filtro, buscar: buscar.trim(), pagina: 1 });
        }}
      >
        <select
          aria-label="Tipo de documento"
          value={filtro.tipo ?? ''}
          onChange={(e) => setFiltro({ ...filtro, tipo: e.target.value as TipoDocumento | '', pagina: 1 })}
        >
          <option value="">Todos los tipos</option>
          {(Object.keys(TIPO) as TipoDocumento[]).map((t) => (
            <option key={t} value={t}>{TIPO[t]}</option>
          ))}
        </select>
        <input className="fecha" type="date" aria-label="Desde" value={filtro.desde ?? ''} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value, pagina: 1 })} />
        <input className="fecha" type="date" aria-label="Hasta" value={filtro.hasta ?? ''} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value, pagina: 1 })} />
        <input placeholder="N.º de venta, número, cliente o CUFE" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar documento" />
        <button className="btn btn--secundario" type="submit">Buscar</button>
      </form>

      {!pagina && !error && <div className="cargando">Cargando documentos…</div>}
      {pagina && pagina.items.length === 0 && <div className="vacio">No hay documentos con estos filtros.</div>}
      {pagina && pagina.items.length > 0 && (
        <div className="tabla-scroll">
          <table className="tabla tabla--clic">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Documento</th>
                <th>Venta</th>
                <th>Cliente</th>
                <th className="num">Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {pagina.items.map((d) => (
                <tr key={d.id} onClick={() => setAbierto(d.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setAbierto(d.id)}>
                  <td>{fechaHora(d.fecha)}</td>
                  <td>
                    {TIPO[d.tipo]}
                    <div className="mono texto-sec">{d.numero}</div>
                  </td>
                  <td className="mono">{d.nFactura}</td>
                  <td>{d.receptor ?? 'Consumidor final'}</td>
                  <td className="num">{usd(d.total)}</td>
                  <td>
                    <Pill tono={ESTADO[d.estado].tono}>{ESTADO[d.estado].texto}</Pill>
                    {d.anulacion && <div className="texto-sec mini">{ANULACION[d.anulacion] ?? d.anulacion}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagina && totalPaginas > 1 && (
        <div className="paginacion">
          <Boton tipo="secundario" deshabilitado={(filtro.pagina ?? 1) <= 1} onClick={() => setFiltro({ ...filtro, pagina: (filtro.pagina ?? 1) - 1 })}>
            Anterior
          </Boton>
          <span className="texto-sec">Página {filtro.pagina ?? 1} de {totalPaginas}</span>
          <Boton tipo="secundario" deshabilitado={(filtro.pagina ?? 1) >= totalPaginas} onClick={() => setFiltro({ ...filtro, pagina: (filtro.pagina ?? 1) + 1 })}>
            Siguiente
          </Boton>
        </div>
      )}

      {abierto !== null && (
        <DetalleDocumento api={api} idEmpresa={idEmpresa} id={abierto} onCerrar={() => setAbierto(null)} onCambio={cargar} />
      )}
    </section>
  );
}

function FacturasNoCargadas(p: { api: FacturacionApi; idEmpresa: number; errores: ErrorCarga[]; onCambio: (e: ErrorCarga[]) => void }) {
  const [cargando, setCargando] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ok, setOk] = useState<number | null>(null);

  async function recargar(n: number) {
    setCargando(n);
    setError(null);
    setOk(null);
    try {
      p.onCambio(await p.api.recargarFactura(p.idEmpresa, n));
      setOk(n);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(null);
    }
  }

  return (
    <div className="panel-aviso">
      <h3>Facturas no cargadas ({p.errores.length})</h3>
      <p className="texto-sec">Estas ventas no entraron a la cola de facturación electrónica. Corrija la causa y vuelva a cargarlas.</p>
      <ErrorApi error={error} />
      {ok !== null && <Alerta tipo="exito" titulo={`La venta ${ok} se cargó y se enviará en los próximos minutos.`} />}
      <ul className="lista-errores">
        {p.errores.map((e) => (
          <li key={e.id}>
            <div>
              <b>{e.origen ?? 'Documento'} · venta {e.nFactura ?? '—'}</b> <span className="texto-sec">{fechaHora(e.fecha)}</span>
              <div className="texto-sec">{e.mensaje}</div>
            </div>
            {e.origen === 'Factura' && e.nFactura !== null && (
              <Boton tipo="secundario" cargando={cargando === e.nFactura} onClick={() => recargar(e.nFactura!)}>
                Volver a cargar
              </Boton>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DetalleDocumento(p: { api: FacturacionApi; idEmpresa: number; id: number; onCerrar: () => void; onCambio: () => void }) {
  const [d, setD] = useState<DocumentoDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accion, setAccion] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    p.api.documento(p.idEmpresa, p.id).then(setD).catch(setError);
  }, [p.api, p.idEmpresa, p.id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && p.onCerrar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [p]);

  async function ejecutar(nombre: string, fn: () => Promise<DocumentoDetalle | void>, mensaje: string) {
    setAccion(nombre);
    setError(null);
    setAviso(null);
    try {
      const r = await fn();
      if (r) setD(r);
      setAviso(mensaje);
      p.onCambio();
    } catch (e) {
      setError(e);
    } finally {
      setAccion(null);
    }
  }

  async function descargar(tipo: 'pdf' | 'xml') {
    // La ventana se abre en el clic (si se abre después de esperar a la API, el navegador la bloquea).
    const ventana = window.open('', '_blank');
    setAccion(tipo);
    setError(null);
    try {
      // Alanube entrega enlaces que vencen en 60 minutos: se piden en el momento.
      const a = await p.api.archivos(p.idEmpresa, p.id);
      const url = tipo === 'pdf' ? a.pdf : a.xml;
      if (!url) throw new Error('Archivo no disponible');
      if (ventana) {
        ventana.opener = null;
        ventana.location.href = url;
      } else window.location.href = url;
    } catch (e) {
      ventana?.close();
      setError(e);
    } finally {
      setAccion(null);
    }
  }

  const doc = d?.documento;

  return (
    <div className="cajon-fondo" onClick={p.onCerrar}>
      <aside className="cajon" role="dialog" aria-modal="true" aria-label="Detalle del documento" onClick={(e) => e.stopPropagation()}>
        <div className="cajon__cab">
          <div>
            <p className="eyebrow">{doc ? TIPO[doc.tipo] : 'Documento'}</p>
            <h2 className="mono">{doc?.numero ?? '…'}</h2>
          </div>
          <button type="button" className="btn btn--texto" onClick={p.onCerrar} aria-label="Cerrar">Cerrar ✕</button>
        </div>

        {!d && !error && <div className="cargando">Cargando…</div>}
        <ErrorApi error={error} />
        {aviso && <Alerta tipo="exito" titulo={aviso} />}

        {doc && d && (
          <div className="cajon__cuerpo">
            <div className="estado-linea">
              <Pill tono={ESTADO[doc.estado].tono}>{ESTADO[doc.estado].texto}</Pill>
              <span className="texto-sec">{ESTADO[doc.estado].ayuda}</span>
            </div>

            {doc.error && (doc.estado === 'ERROR' || doc.estado === 'RECHAZADO') && (
              <Alerta tipo="error" titulo="Motivo">{doc.error}</Alerta>
            )}
            {doc.totalOrigen !== null && Math.abs(doc.totalOrigen - doc.total) >= 0.01 && (
              <Alerta tipo="aviso" titulo="Diferencia de redondeo">
                Total de la venta {usd(doc.totalOrigen)} · total electrónico {usd(doc.total)}. La DGI tolera diferencias menores a
                USD 0.50.
              </Alerta>
            )}
            {doc.anulacion && (
              <Alerta tipo={doc.anulacion === 'ERROR' ? 'error' : 'info'} titulo={ANULACION[doc.anulacion] ?? doc.anulacion}>
                {d.anulacionMotivo && <div>Motivo: {d.anulacionMotivo}</div>}
                {d.anulacionError && <div>{d.anulacionError}</div>}
              </Alerta>
            )}

            <div className="acciones acciones--inicio">
              <Boton tipo="secundario" onClick={() => descargar('pdf')} cargando={accion === 'pdf'} deshabilitado={!doc.cufe && doc.estado !== 'AUTORIZADO'}>
                Descargar PDF
              </Boton>
              <Boton tipo="secundario" onClick={() => descargar('xml')} cargando={accion === 'xml'} deshabilitado={!doc.cufe && doc.estado !== 'AUTORIZADO'}>
                Descargar XML
              </Boton>
              {doc.estado === 'ERROR' && (
                <Boton onClick={() => ejecutar('reintentar', () => p.api.reintentar(p.idEmpresa, p.id), 'El documento volvió a la cola y se enviará en los próximos minutos.')} cargando={accion === 'reintentar'}>
                  Reintentar
                </Boton>
              )}
            </div>

            <dl className="meta meta--detalle">
              <dt>Venta (POS)</dt><dd className="mono">{doc.nFactura}</dd>
              <dt>Fecha</dt><dd>{fechaHora(doc.fecha)}</dd>
              <dt>Cliente</dt><dd>{doc.receptor ?? 'Consumidor final'}{doc.receptorId && <span className="mono texto-sec"> · {doc.receptorId}</span>}</dd>
              <dt>Total</dt><dd><b>{usd(doc.total)}</b></dd>
              {d.referencia && (<><dt>Referencia</dt><dd>{d.referencia}</dd></>)}
              {d.motivo && (<><dt>Motivo</dt><dd>{d.motivo}</dd></>)}
              <dt>CUFE</dt><dd className="mono quiebre">{doc.cufe ?? '—'}</dd>
              <dt>Aprobación DGI</dt><dd>{fechaHora(doc.fechaAutorizacion)}</dd>
            </dl>

            <h3>Detalle</h3>
            <div className="tabla-scroll">
              <table className="tabla">
                <thead>
                  <tr><th>Descripción</th><th className="num">Cant.</th><th className="num">Precio</th><th className="num">ITBMS</th><th className="num">Total</th></tr>
                </thead>
                <tbody>
                  {d.lineas.map((l) => (
                    <tr key={l.numero}>
                      <td>{l.descripcion}<div className="mono texto-sec mini">CPBS {l.cpbs ?? '—'} · tasa {l.tasaItbms}</div></td>
                      <td className="num">{l.cantidad}</td>
                      <td className="num">{l.precioUnitario.toFixed(2)}</td>
                      <td className="num">{l.itbms.toFixed(2)}</td>
                      <td className="num">{l.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {d.mensajes.length > 0 && (
              <>
                <h3>Mensajes del PAC y la DGI</h3>
                <ul className="mensajes">{d.mensajes.map((m, i) => <li key={i} className="mono">{m}</li>)}</ul>
              </>
            )}

            <h3>Correo al cliente</h3>
            {d.correos.length === 0 ? (
              <p className="texto-sec">
                {doc.estado === 'AUTORIZADO'
                  ? d.receptorEmail ? 'Sin envíos registrados.' : 'El cliente no tiene correo registrado.'
                  : 'Se envía cuando la DGI apruebe el documento.'}
              </p>
            ) : (
              <ul className="mensajes">
                {d.correos.map((c, i) => (
                  <li key={i}>
                    {c.email} · <b>{c.estado === 'ENVIADO' ? 'Enviado' : c.estado === 'ERROR' ? 'Error' : 'Pendiente'}</b>{' '}
                    <span className="texto-sec">{fechaHora(c.fechaEnvio ?? c.fecha)}</span>
                    {c.error && <div className="texto-sec mini">{c.error}</div>}
                  </li>
                ))}
              </ul>
            )}
            {doc.estado === 'AUTORIZADO' && (
              <form
                className="reenvio"
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = email.trim();
                  if (v && !RX.email.test(v)) return setEmailError('Correo inválido.');
                  if (!v && !d.receptorEmail) return setEmailError('Indique el correo.');
                  setEmailError(null);
                  ejecutar('correo', () => p.api.reenviarCorreo(p.idEmpresa, p.id, v || null), 'Correo en cola: se enviará en los próximos minutos.');
                }}
              >
                <Campo etiqueta="Reenviar a" error={emailError} ayuda={d.receptorEmail ? `Vacío = ${d.receptorEmail}` : undefined}>
                  {(id) => <input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@cliente.com" />}
                </Campo>
                <Boton submit cargando={accion === 'correo'}>Reenviar correo</Boton>
              </form>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
